// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';
import { openSnackbar } from './snackbar';

// ----------------------------------------------------------------------

const initialState = {
    total: {
        all: 0,
        items: 0
    },
    list: [],
    loading: false,
    pagination: {
        pageNumber: 0,
        pageSize: 10
    },
    filter: {
        showInactive: false
    }
};

const slice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUsers(state, action) {
            state.list = action.payload?.data || [];
            state.total.all = action.payload?.pagination?.total || 0;
            state.total.items = action.payload?.pagination?.totalItems || 0;
        },
        setPagination(state, action) {
            state.pagination = action.payload;
        },
        setFilter(state, action) {
            state.filter = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function fetchUsers(params) {
    return async (dispatch, getState) => {
        const { pagination, filter } = getState().user;

        dispatch(slice.actions.setLoading(true));

        // Merge provided params with current pagination
        const query = {
            pagination: params?.pagination || pagination,
            search: params?.search || '',
            ...(params?.filter ? params.filter : filter)
        };
        const response = await axios.get('/api/v2/user', {
            params: { ...query.pagination, ...query }
        });
        dispatch(slice.actions.setUsers(response.data));

        if (params?.pagination) {
            dispatch(slice.actions.setPagination(params.pagination));
        }
        if (params?.filter) {
            dispatch(slice.actions.setFilter(params.filter));
        }

        dispatch(slice.actions.setLoading(false));
    };
}

export function createUser(data) {
    return async (dispatch) => {
        await axios.post('/api/v2/user', data);

        // Optionally, you might want to fetch the updated list of users here
        dispatch(fetchUsers());
    };
}

export function editUser(userId, data) {
    return async (dispatch) => {
        await axios.patch(`/api/v2/user/${userId}`, data);

        // Optionally, you might want to fetch the updated list of users here
        dispatch(fetchUsers());
    };
}

export function resetPassword(userId) {
    return async (dispatch) => {
        await axios.patch(`/api/v2/user/reset-password/${userId}`);
        dispatch(fetchUsers());
    };
}

export function requestPasswordReset(groupId = '', email, hasCaptcha = false) {
    return async (dispatch) => {
        dispatch(
            openSnackbar({
                open: true,
                message: 'Sending password reset request...',
                variant: 'progress'
            })
        );
        try {
            const response = await axios.post('/api/v2/auth/request-reset-password', { groupId, email, hasCaptcha });
            const result = response.data;
            dispatch(
                openSnackbar({
                    open: true,
                    title: 'Password Reset Request Succeeded',
                    message: 'Follow the email instructions to reset your password',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
            return result;
        } catch (err) {
            console.log('err', err);
            const { code = null } = err;
            if (code) {
                const result = err;
                // check what type of code
                if (result.code === 'too.many.request') {
                    dispatch(
                        openSnackbar({
                            open: true,
                            title: 'Password Reset Failed.',
                            message: result.message,
                            variant: 'alert',
                            alert: {
                                color: 'error'
                            }
                        })
                    );
                } else if (result.code === 'show-captcha') {
                    dispatch(
                        openSnackbar({
                            open: false
                        })
                    );
                }
                return result;
            } else {
                dispatch(
                    openSnackbar({
                        open: true,
                        title: 'Password Reset Failed.',
                        message: err?.message || 'Please check your internet connection.',
                        variant: 'alert',
                        alert: {
                            color: 'error'
                        }
                    })
                );
            }
        }
    };
}
